import { BffApiClientMolecule } from "@/app/api-client/store"
import { usePercentage } from "@/app/percentage/utils"
import { PriceWrapper } from "@/app/price"
import type { NftId } from "@anaconda/core/nft/types"
import type { PriceValue } from "@anaconda/core/price/types"
import { Trans } from "@lingui/react/macro"
import { Card, Skeleton } from "@rarible_int/anaconda_ui_components"
import { type UseQueryResult, useQuery } from "@tanstack/react-query"
import Big from "big.js"
import { useMolecule } from "bunshi/react"
import { getRoyaltyQueryParams } from "../store/get-royalty-query-params"

interface Props {
  nftId: NftId
  price: PriceValue
  serviceFeeQuery?: {
    isLoading: boolean
    data: number | undefined
    error: Error | null
  }
}

export function PriceQuote({ nftId, price, serviceFeeQuery }: Props) {
  const royaltyQuery = useRoyaltyQuery(nftId)

  return (
    <Card className="flex flex-col gap-2 p-4">
      <div className="flex flex-col gap-1 border-b pb-2 text-2xs">
        <QuoteItem name={<Trans>Price</Trans>} value={<Price {...price} />} />
        <Royalty royaltyQuery={royaltyQuery} price={price} />
        <ServiceFee serviceFeeQuery={serviceFeeQuery} price={price} />
      </div>
      <YouWillReceiveItem
        serviceFee={serviceFeeQuery?.data}
        price={price}
        royalty={royaltyQuery.data?.royalty}
      />
    </Card>
  )
}

export function QuoteItem({
  name,
  value,
}: { name: React.ReactNode; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-foreground/60">{name}</span>
      {value}
    </div>
  )
}

export function QuoteItemSkeleton() {
  return <Skeleton className="h-[18px] w-full" />
}

function Royalty({
  royaltyQuery,
  price,
}: {
  royaltyQuery: UseQueryResult<
    {
      royalty: number
    },
    Error
  >
  price: PriceValue
}) {
  if (royaltyQuery.isLoading) return <QuoteItemSkeleton />

  const royalty = royaltyQuery.data?.royalty

  return (
    <FeeItem fee={royalty} price={price} label={<Trans>Royalties</Trans>} />
  )
}

function ServiceFee({
  serviceFeeQuery,
  price,
}: {
  serviceFeeQuery?: {
    isLoading: boolean
    data: number | undefined
    error: Error | null
  }
  price: PriceValue
}) {
  if (!serviceFeeQuery) return null
  if (serviceFeeQuery.isLoading) return <QuoteItemSkeleton />

  const serviceFee = serviceFeeQuery.data

  return (
    <FeeItem
      fee={serviceFee}
      price={price}
      label={<Trans>Service fee</Trans>}
    />
  )
}

function YouWillReceiveItem({
  price,
  royalty = 0,
  serviceFee = 0,
}: {
  price: PriceValue
  royalty?: number
  serviceFee?: number
}) {
  const willReceiveAmount = Big(price.amount)
    .mul(100 - royalty - serviceFee)
    .div(100)
    .toString()

  return (
    <QuoteItem
      name={<Trans>You will receive</Trans>}
      value={<Price amount={willReceiveAmount} currency={price.currency} />}
    />
  )
}

function YouWillPayItem({
  price,
  serviceFee = 0,
}: {
  price: PriceValue
  serviceFee?: number
}) {
  const totalAmount = Big(price.amount)
    .mul(100 + serviceFee)
    .div(100)
    .toString()

  return (
    <QuoteItem
      name={<Trans>You will pay</Trans>}
      value={<Price amount={totalAmount} currency={price.currency} />}
    />
  )
}

export function FeeItem({
  fee,
  price,
  label,
}: {
  fee?: number
  price: PriceValue
  label: React.ReactNode
}) {
  if (!fee)
    return (
      <QuoteItem
        name={label}
        value={<span className="text-foreground/100">—</span>}
      />
    )

  const percentage = usePercentage(fee)
  const feeAmount = Big(price.amount).mul(fee).div(100).toString()

  return (
    <QuoteItem
      name={label}
      value={
        <div className="flex items-center gap-2">
          <span className="text-foreground/60">{percentage}</span>
          <span className="text-foreground">
            {<Price amount={feeAmount} currency={price.currency} />}
          </span>
        </div>
      }
    />
  )
}

function Price(props: PriceValue) {
  return <PriceWrapper currencyClassName="text-foreground" {...props} />
}

function useRoyaltyQuery(nftId: NftId) {
  const apiClient = useMolecule(BffApiClientMolecule)

  return useQuery(getRoyaltyQueryParams(apiClient, nftId))
}

export { YouWillPayItem }

// Purchase-specific price quote component
export function PurchasePriceQuote({ nftId, price, serviceFeeQuery }: Props) {
  const royaltyQuery = useRoyaltyQuery(nftId)

  return (
    <Card className="flex flex-col gap-2 p-4">
      <div className="flex flex-col gap-1 border-b pb-2 text-2xs">
        <QuoteItem name={<Trans>Price</Trans>} value={<Price {...price} />} />
        <ServiceFee serviceFeeQuery={serviceFeeQuery} price={price} />
      </div>
      <YouWillPayItem serviceFee={serviceFeeQuery?.data} price={price} />
    </Card>
  )
}
